import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FiArrowLeft, FiBookOpen } from 'react-icons/fi';
import api from '../config/api';
import { usePermissions } from '../context/PermissionsContext';
import { usePageTitle } from '../context/PageChromeContext';
import IconButton from '../components/IconButton';
import JournalEditor from '../components/erp/JournalEditor';
import { queryKeys } from '../config/queryKeys';
import { appAlert } from '../utils/appDialog';
import './Services.css';
import '../components/erp/Erp.css';

/** Full-page new general ledger journal entry. */
const JournalEntryDetails = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('erp_journals_manage');
  const canPost = hasPermission('erp_journals_post');
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get('company_id') || '';

  usePageTitle('New journal entry');

  const accounts = useQuery(
    [...queryKeys.accountingAccounts, companyId || 'none'],
    async () =>
      (
        await api.get('/accounting/accounts', {
          params: companyId ? { company_id: companyId } : undefined,
        })
      ).data,
    { enabled: canManage }
  );

  const withCompany = (payload) => ({
    ...payload,
    company_id: payload.company_id || companyId || undefined,
  });

  const saveJournal = useMutation(
    async (payload) => (await api.post('/accounting/journals', withCompany(payload))).data,
    {
      onSuccess: (created) => {
        qc.invalidateQueries(queryKeys.accountingJournals);
        qc.invalidateQueries('erp-journals');
        void appAlert(
          created?.entry_number
            ? `Draft ${created.entry_number} saved.`
            : 'Journal draft saved.'
        );
        navigate('/erp/accounting?tab=Journals', { replace: true });
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const saveAndPost = useMutation(
    async (payload) => {
      const draft = (await api.post('/accounting/journals', withCompany(payload))).data;
      if (draft?.id && canPost) {
        await api.post(`/accounting/journals/${draft.id}/post`);
      }
      return draft;
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(queryKeys.accountingJournals);
        qc.invalidateQueries('erp-journals');
        navigate('/erp/accounting?tab=Journals', { replace: true });
      },
      onError: (e) => void appAlert(e?.response?.data?.error || e.message),
    }
  );

  const back = () => navigate('/erp/accounting?tab=Journals');

  if (!canManage) {
    return (
      <div className="svc-package-page list-page erp-page">
        <div className="svc-package-toolbar page-toolbar">
          <IconButton icon={FiArrowLeft} label="Back" onClick={back} />
        </div>
        <div className="svc-package-page-card">
          <div className="error">You do not have permission to create journal entries.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="svc-package-page list-page erp-page">
      <div className="svc-package-toolbar page-toolbar">
        <IconButton icon={FiArrowLeft} label="Back to journals" onClick={back} />
      </div>
      <div className="svc-package-page-card">
        <div className="svc-package-page-header">
          <h1>
            <FiBookOpen aria-hidden /> New journal entry
          </h1>
          <p className="page-subtitle">
            Double-entry draft. Debits must equal credits before a draft can post to the ledger.
          </p>
        </div>
        <JournalEditor
          accounts={accounts.data || []}
          saving={saveJournal.isLoading || saveAndPost.isLoading}
          onSave={(payload) => saveJournal.mutate(payload)}
          onPost={canPost ? (payload) => saveAndPost.mutate(payload) : undefined}
        />
      </div>
    </div>
  );
};

export default JournalEntryDetails;
